import React from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { useAuth } from "./AuthContext";
import Footer from "./Footer";

export default function TermsOfService() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  return (
    <>
      <div className="w-full flex justify-center text-[#eee] bg-[#111]">
        <div className="w-full max-w-[1100px] px-6 md:px-4">
          {/* Header */}
          <div className="w-full flex flex-row items-center justify-between mt-6">
            <div
              onClick={() => navigate("/")}
              className="flex flex-row items-center mb-6 gap-x-2 cursor-pointer"
            >
              <h1 className="text-3xl font-bold font-bebas tracking-wider">
                PLAYSET
              </h1>
            </div>

            <button
              onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
              className="px-4 py-2 bg-yellow-500/20 border border-yellow-500 text-white rounded-xl cursor-pointer hover:bg-yellow-500 transition"
            >
              {currentUser ? "Dashboard" : "Sign In"}
            </button>
          </div>

          {/* Title */}
          <div className="font-bebas text-4xl text-[#fff] font-bold leading-snug tracking-wider leading-[5rem] md:leading-none">
            Terms of Service
          </div>

          <div className="font-opensans space-y-10 mb-10">
            <p className="my-6 opacity-70">
              <strong>Last Updated:</strong> 11th January 2026
            </p>

            {/* Intro */}
            <div className="space-y-2">
              <p>
                These terms of service govern your use of{" "}
                <strong>Playset</strong> (
                <a className="underline" href="https://playset.app">
                  https://playset.app
                </a>
                ). Playset is a game testing and playtest management platform.
                By accessing our site, you agree to these terms. Please read
                them carefully.
              </p>
            </div>

            {/* Account & Usage */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Account & Acceptable Use</h3>
              <p>
                You are responsible for maintaining the confidentiality of your
                account credentials and for all activities that occur under your
                account. You agree to use Playset only for lawful purposes and in
                accordance with these terms.
              </p>

              <p className="mt-2">
                <strong>You must not:</strong>
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>Use Playset for any illegal or unauthorized purpose.</li>
                <li>
                  Distribute malware, viruses, or malicious game builds.
                </li>
                <li>
                  Disrupt or interfere with the operation of Playset or its
                  infrastructure.
                </li>
                <li>Harass or intimidate other users or testers.</li>
                <li>
                  Share another user's game builds or playtest data without
                  permission.
                </li>
                <li>Attempt to gain unauthorized access to Playset systems.</li>
                <li>
                  Remove or obscure any proprietary notices or labels on
                  Playset's content.
                </li>
              </ul>
            </div>

            {/* Game Builds & Content */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Game Builds & User Content</h3>
              <p>
                You retain all rights to game builds and content you upload to
                Playset. By uploading content, you grant Playset a license to
                store, transmit, and display your content as necessary to provide
                the playtest service.
              </p>

              <p className="mt-2">
                You represent and warrant that you own or have the necessary
                rights to all content you upload. You are responsible for
                ensuring that your game builds do not violate any third-party
                rights, including intellectual property rights.
              </p>

              <p className="mt-2">
                Playset is not responsible for:
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>
                  Copyright or trademark infringement claims related to your
                  builds.
                </li>
                <li>Loss or corruption of your game builds or data.</li>
                <li>
                  Unauthorized access to your builds by testers you have
                  invited.
                </li>
              </ul>
            </div>

            {/* Your Privacy */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Your Privacy</h3>
              <p>
                Please review our{" "}
                <a className="underline" href="/privacypolicy">
                  Privacy Policy
                </a>{" "}
                to understand how we collect, use, and protect your information.
              </p>
            </div>

            {/* Subscriptions & Billing */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Subscriptions & Billing</h3>
              <p>
                Playset offers subscription plans (Free, Starter, Studio) with
                different limits on game uploads, playtest duration, and tester
                limits.
              </p>
              <p>
                Paid subscriptions renew automatically on a monthly or annual
                basis according to the plan selected. You can cancel your
                subscription at any time from your account settings. Cancellation
                takes effect at the end of your current billing period.
              </p>
              <p>
                <strong>Billing & Payments:</strong> All charges are billed to
                the payment method you provide. You authorize Playset to charge
                your payment method for subscription fees and any additional
                charges. Stripe handles all payment processing securely.
              </p>
            </div>

            {/* Refund Policy */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Refund Policy</h3>
              <p>
                Subscription charges are non-refundable. If you have a billing
                issue or dispute, please contact our support team. Refunds are
                provided only as required by law or at Playset's sole discretion.
              </p>
              <p>
                If you cancel your subscription, you retain access until the end
                of your billing period. Upon account termination, your data will
                be deleted and cannot be recovered.
              </p>
            </div>

            {/* Termination & Account Deletion */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Termination</h3>
              <p>
                Playset may suspend or terminate your account at any time,
                without notice, if you violate these terms or engage in
                prohibited conduct. Termination for violation of these terms will
                not result in a refund.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by law, Playset is provided
                &quot;as is&quot; without warranties of any kind, express or
                implied. Playset disclaims all warranties including fitness for a
                particular purpose.
              </p>
              <p className="mt-2">
                Playset shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages arising from your use
                of or inability to use Playset, including loss of data,
                interruption of service, or business losses.
              </p>
            </div>

            {/* Service Availability */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Service Availability</h3>
              <p>
                Playset is provided on a best-effort basis. We do not guarantee
                uninterrupted or error-free service. We may perform maintenance,
                updates, or upgrades that may temporarily interrupt service.
              </p>
              <p>
                We are not liable for data loss, service interruptions, or
                inability to access your builds or playtest sessions due to
                technical issues, maintenance, or force majeure events.
              </p>
            </div>

            {/* Changes to These Terms */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Changes to These Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. Material
                changes will be announced via email or prominent notice on the
                platform. Continued use of Playset constitutes acceptance of
                modified terms. It is your responsibility to review this page
                periodically for updates.
              </p>
            </div>

            {/* Contact Us */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Contact Us</h3>
              <p>
                If you have any questions about these terms or our service,
                please contact us from the{" "}
                <a className="underline" href="/support">
                  Support
                </a>{" "}
                page.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}
